import { useCallback, useEffect, useRef, useState, type ChangeEvent, type FormEvent } from 'react';

import { usePageTitle } from '../hooks/usePageTitle';
import './Team.css';

/* ── Roster & match facts (from the ALTA site) ─────────────────────────── */

interface Player {
  name: string;
  /** Cookout duty, when assigned. ALTA's "Bring" field is N/A for Week 1. */
  bring?: string;
}

interface Line {
  id: string;
  /** Null until the captain publishes the week's lineup on the ALTA site. */
  players: [Player, Player] | null;
}

// Week 1 lineup, read off the ALTA lineup grid on the morning of Sep 12 for
// Seven Hills. Five courts in this league. Everything downstream (score rows,
// the blank scoreboard, the line tally, who is exempt from the Hall of Shame)
// sizes itself off this array; set a line back to null when the next week's
// lineup has not been posted yet. The Pi's feed carries availability only, so
// this one is typed in by hand — check it against ALTA before match day.
const LINEUP: Line[] = [
  { id: '1D', players: [{ name: 'Kenneth Barnes' }, { name: 'Sam Pearce' }] },
  { id: '2D', players: [{ name: 'Chuck Giddens' }, { name: 'Larry Gillen' }] },
  { id: '3D', players: [{ name: 'Daniel Longacre' }, { name: 'Loren Plisco' }] },
  { id: '4D', players: [{ name: 'Bill Tisdale' }, { name: 'Brian Bonsall' }] },
  { id: '5D', players: [{ name: 'Jon Baker' }, { name: 'Stephen Horn, Jr.' }] },
];

/** Everyone the captain actually put on a court this week. */
const LINEUP_NAMES = new Set(LINEUP.flatMap((l) => l.players ?? []).map((p) => p.name));

/* ── Week 1 availability (from the ALTA site) ──────────────────────────── */

type Availability = 'in' | 'out' | 'maybe' | 'silent';

interface RosterEntry {
  name: string;
  role?: string;
  status: Availability;
}

/**
 * When this snapshot was taken off the ALTA availability grid. It is typed in
 * by hand, so say so on the page rather than implying it is live — naming a
 * teammate as a no-show is worth being accurate about.
 */
const ROSTER_AS_OF = 'September 10';

/** The full 25-man roster and where each stands for Week 1 at Seven Hills. */
const WEEK_ONE_ROSTER: RosterEntry[] = [
  { name: 'Chuck Giddens', status: 'in' },
  { name: 'Daniel Longacre', role: 'Captain', status: 'in' },
  { name: 'Jon Baker', status: 'in' },
  { name: 'Jeff Bickerton', status: 'in' },
  { name: 'Brian Bonsall', status: 'in' },
  { name: 'Steve Fleming', status: 'in' },
  { name: 'Larry Gillen', status: 'in' },
  { name: 'Stephen Horn, Jr.', status: 'in' },
  { name: 'Sam Pearce', status: 'in' },
  { name: 'Loren Plisco', status: 'in' },
  { name: 'Bill Tisdale', status: 'in' },
  { name: 'Mark Lind', role: 'Co-Captain', status: 'out' },
  { name: 'Chris Burns', status: 'out' },
  { name: 'Randy Greaser', status: 'out' },
  { name: 'Brett Hellenga', status: 'out' },
  { name: 'Mak Kawano', status: 'out' },
  { name: 'Jonathan Sabo', status: 'out' },
  { name: 'Kevin Stark', status: 'out' },
  { name: 'Kenneth Barnes', status: 'maybe' },
  { name: 'Scott Mackie', status: 'maybe' },
  { name: 'Jeff Adams', status: 'silent' },
  { name: 'Greg Mueller', status: 'silent' },
  { name: 'Jon Sabo', status: 'silent' },
  { name: 'Zachary Waters', status: 'silent' },
  { name: 'Mike Wemyss', status: 'silent' },
];

/** Charges are assigned by position, not at random, so the board is stable. */
const CHARGES = [
  'Failure to click a button',
  'Aggravated silence',
  'Loitering in the group chat',
  'Possession of an unread email',
  'Willful avoidance of a dropdown',
  'Contempt of calendar',
  'Reckless disregard for a Saturday',
  'Obstruction of a lineup',
  'Fleeing the roster',
  'Conduct unbecoming of a doubles partner',
];

/**
 * The Captain and Co-Captain never appear on the Hall of Shame or in the
 * "still waiting on you" count. They build the lineup, so they already know
 * whether they are playing — an empty availability box is not a lapse for
 * them the way it is for everyone else.
 */
const isSkipper = (p: RosterEntry) => p.role === 'Captain' || p.role === 'Co-Captain';

/**
 * Same idea for anyone the captain has already put on a court: he is playing
 * at 9:00 AM whatever his availability box says, so an undecided face is not
 * something to induct him for.
 */
const isPlaying = (p: RosterEntry) => LINEUP_NAMES.has(p.name);

interface RosterGroups {
  available: RosterEntry[];
  unavailable: RosterEntry[];
  maybes: RosterEntry[];
  silent: RosterEntry[];
  /** Everyone who gave a straight answer either way — the people to thank. */
  responded: RosterEntry[];
  /**
   * Nobody who has given a yes or a no. That includes the "maybe" crowd, who
   * clicked something without committing to anything, so they are inducted
   * under a gentler heading than the total no-shows.
   */
  hallOfShame: RosterEntry[];
  total: number;
}

function groupRoster(roster: RosterEntry[]): RosterGroups {
  const available = roster.filter((p) => p.status === 'in');
  const unavailable = roster.filter((p) => p.status === 'out');
  const maybes = roster.filter((p) => p.status === 'maybe');
  const silent = roster.filter((p) => p.status === 'silent');
  return {
    available,
    unavailable,
    maybes,
    silent,
    responded: [...available, ...unavailable],
    hallOfShame: [...silent, ...maybes].filter((p) => !isSkipper(p) && !isPlaying(p)),
    total: roster.length,
  };
}

const SETS_PER_LINE = 3;

/* ── Live roster feed ──────────────────────────────────────────────────── */

/**
 * The Second Brain Pi scrapes ALTA on a schedule and publishes a token-gated,
 * read-only projection of it. The browser calls our own same-origin
 * /api/tennis, and the App Service does the authenticated call — the Pi's
 * token never reaches a browser. If any of that is down, the page falls back
 * to WEEK_ONE_ROSTER above, so it always renders something true-as-of-then.
 */
interface TennisFeed {
  roster: RosterEntry[];
  /** When the Pi last scraped ALTA. Empty when we are on the fallback. */
  fetchedAt: string;
  source: 'live' | 'fallback';
}

function useTennisFeed(): TennisFeed {
  const [feed, setFeed] = useState<TennisFeed>({
    roster: WEEK_ONE_ROSTER,
    fetchedAt: '',
    source: 'fallback',
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/tennis');
        if (!res.ok) return; // 503 until the App Service settings exist
        const data = (await res.json()) as {
          roster?: { name?: string; role?: string; status?: string }[];
          fetched_at?: string;
        };
        const roster = (data.roster ?? [])
          .filter((r) => r.name)
          .map<RosterEntry>((r) => ({
            name: r.name as string,
            role: r.role || undefined,
            status: (['in', 'out', 'maybe', 'silent'].includes(r.status ?? '')
              ? r.status
              : 'silent') as Availability,
          }));
        // An empty roster is a broken feed, not an empty team — keep the
        // fallback rather than rendering a page with nobody on it.
        if (!cancelled && roster.length) {
          setFeed({ roster, fetchedAt: data.fetched_at ?? '', source: 'live' });
        }
      } catch {
        /* keep the fallback */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return feed;
}

/** "3 minutes ago" / "2 hours ago" — how stale is what you are looking at. */
function timeAgo(iso: string, now: Date): string {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return '';
  const mins = Math.floor((now.getTime() - then.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

/* ── Match-day logistics ───────────────────────────────────────────────── */

const VENUE = {
  name: 'Seven Hills Swim & Tennis Club',
  address: '432 Seven Hills Blvd, Dallas, GA 30132',
  courts: '10 lighted hard courts',
  // Universal links: both hand off to the native phone app when it is
  // installed and fall back to the web map when it is not.
  googleMaps:
    'https://www.google.com/maps/dir/?api=1&destination=432+Seven+Hills+Blvd%2C+Dallas%2C+GA+30132',
  appleMaps: 'https://maps.apple.com/?daddr=432+Seven+Hills+Blvd%2C+Dallas%2C+GA+30132&dirflg=d',
};

/**
 * Match-day forecast, pulled from Open-Meteo on the morning of September 12 for
 * Dallas, GA (33.924, -84.841 — the nearest point Open-Meteo geocodes to the
 * Seven Hills courts). The storms that showed up in the two-day-out forecast
 * fell apart; what is left is a damp morning. The card says when it was taken
 * so nobody trusts a stale number.
 */
const WEATHER = {
  pulledOn: 'this morning',
  headline: 'Drizzle at warm-up, drying out after',
  chance: 62,
  rainInches: 0.09,
  high: 82,
  low: 72,
  gusts: 18,
  dryFrom: '1 PM',
  hours: [
    { time: '8 AM', temp: 72, pop: 50, storm: false, note: 'Damp' },
    { time: '9 AM', temp: 73, pop: 62, storm: false, note: 'Warm-up' },
    { time: '10 AM', temp: 74, pop: 61, storm: false, note: 'Drizzle' },
    { time: '11 AM', temp: 74, pop: 52, storm: false },
    { time: '12 PM', temp: 75, pop: 45, storm: false },
    { time: '1 PM', temp: 78, pop: 30, storm: false, note: 'Drying' },
    { time: '2 PM', temp: 79, pop: 19, storm: false },
    { time: '3 PM', temp: 78, pop: 17, storm: false, note: 'Sun' },
  ],
};

/* ── Season & schedule (from the ALTA site) ────────────────────────────── */

const SEASON = {
  name: '2026 Fall Men B-5',
  club: 'TOWNE LAKE HILLS',
  rosterId: '387559',
  division: '1 of 8',
  coordinator: 'Anthony Depa',
  captain: 'Daniel Longacre',
  coCaptain: 'Mark Lind',
} as const;

interface Match {
  week: number;
  /** ISO day, used for ordering and for picking the next match up. */
  iso: string;
  label: string;
  /** True when we travel; false means we host. */
  away?: boolean;
  opponent?: string;
  opponentCaptain?: string;
  bye?: boolean;
}

const SCHEDULE: Match[] = [
  { week: 1, iso: '2026-09-12', label: 'Sat, Sep 12', away: true, opponent: 'Seven Hills', opponentCaptain: 'Maloney' },
  { week: 2, iso: '2026-09-19', label: 'Sat, Sep 19', away: true, opponent: 'Dellinger Park', opponentCaptain: 'Still' },
  { week: 3, iso: '2026-09-26', label: 'Sat, Sep 26', opponent: 'Deer Run West', opponentCaptain: 'Gaudette' },
  { week: 4, iso: '2026-10-03', label: 'Sat, Oct 3', opponent: 'Camden Pointe', opponentCaptain: 'Page' },
  { week: 5, iso: '2026-10-10', label: 'Sat, Oct 10', away: true, opponent: 'Burnt Hickory Registry', opponentCaptain: 'Lewis' },
  { week: 6, iso: '2026-10-17', label: 'Sat, Oct 17', opponent: 'Creekside Golf & CC', opponentCaptain: 'Wendling' },
  { week: 7, iso: '2026-10-24', label: 'Sat, Oct 24', bye: true },
];

/** Local calendar day as an ISO string — avoids the UTC shift toDateString would hide. */
function todayIso(now: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** The next match still to be played, or undefined once the season is done. */
function upcomingMatch(now: Date): Match | undefined {
  const today = todayIso(now);
  return SCHEDULE.find((m) => !m.bye && m.iso >= today);
}

/** Whole days from today until an ISO day; negative once it is past. */
function daysUntil(iso: string, now: Date): number {
  const [y, m, d] = iso.split('-').map(Number);
  const target = new Date(y, m - 1, d);
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((target.getTime() - start.getTime()) / 86_400_000);
}

/* ── Score data, shared via an Azure Storage blob ──────────────────────── */

interface SetScore {
  us: number | null;
  them: number | null;
}

interface ScoreData {
  version: 1;
  /** Which season these numbers belong to. The blob is reused season to
      season, so anything stamped for a previous one is treated as empty
      rather than shown as this season's result. */
  season: string;
  updatedAt: string;
  lines: SetScore[][];
}

const SEASON_KEY = '2026-fall';

function emptyScores(): ScoreData {
  return {
    version: 1,
    season: SEASON_KEY,
    updatedAt: '',
    lines: LINEUP.map(() =>
      Array.from({ length: SETS_PER_LINE }, () => ({ us: null, them: null })),
    ),
  };
}

/** Never trust what comes back from storage — coerce it into shape. */
function sanitize(raw: unknown): ScoreData {
  const data = emptyScores();
  if (typeof raw !== 'object' || raw === null) return data;
  // Last season's scores are still sitting in the blob until someone posts
  // over them; an unstamped or stale payload reads as a fresh slate.
  if ((raw as { season?: unknown }).season !== SEASON_KEY) return data;
  const lines = (raw as { lines?: unknown }).lines;
  if (!Array.isArray(lines)) return data;

  lines.slice(0, LINEUP.length).forEach((line, i) => {
    if (!Array.isArray(line)) return;
    line.slice(0, SETS_PER_LINE).forEach((set, j) => {
      const us = (set as SetScore)?.us;
      const them = (set as SetScore)?.them;
      data.lines[i][j] = {
        us: typeof us === 'number' && us >= 0 && us <= 99 ? Math.round(us) : null,
        them: typeof them === 'number' && them >= 0 && them <= 99 ? Math.round(them) : null,
      };
    });
  });
  return data;
}

/** Storage endpoints from runtime config, so they can change without a rebuild. */
interface ScoreStoreConfig {
  blobUrl: string;
  sas: string;
}

interface TablesConfig {
  endpoint: string;
  usersSas: string;
  messagesSas: string;
}

/** The blob folder message photos are uploaded to and served from. */
interface PhotosConfig {
  baseUrl: string;
  sas: string;
}

interface RuntimeSettings {
  scores: ScoreStoreConfig | null;
  tables: TablesConfig | null;
  photos: PhotosConfig | null;
}

async function loadRuntimeSettings(): Promise<RuntimeSettings> {
  try {
    const response = await fetch('/appsettings.json', { cache: 'no-store' });
    if (!response.ok) return { scores: null, tables: null, photos: null };
    const settings = await response.json();
    const ts = settings?.TeamScores ?? {};
    const scores =
      typeof ts.BlobUrl === 'string' && ts.BlobUrl && typeof ts.Sas === 'string' && ts.Sas
        ? { blobUrl: ts.BlobUrl, sas: String(ts.Sas).replace(/^\?/, '') }
        : null;
    const tables =
      typeof ts.TableEndpoint === 'string' &&
      ts.TableEndpoint &&
      typeof ts.UsersSas === 'string' &&
      ts.UsersSas &&
      typeof ts.MessagesSas === 'string' &&
      ts.MessagesSas
        ? {
            endpoint: String(ts.TableEndpoint).replace(/\/$/, ''),
            usersSas: String(ts.UsersSas).replace(/^\?/, ''),
            messagesSas: String(ts.MessagesSas).replace(/^\?/, ''),
          }
        : null;
    const photos =
      typeof ts.PhotosBaseUrl === 'string' &&
      ts.PhotosBaseUrl &&
      typeof ts.PhotosSas === 'string' &&
      ts.PhotosSas
        ? {
            baseUrl: String(ts.PhotosBaseUrl).replace(/\/$/, ''),
            sas: String(ts.PhotosSas).replace(/^\?/, ''),
          }
        : null;
    return { scores, tables, photos };
  } catch {
    return { scores: null, tables: null, photos: null };
  }
}

function photoUrl(photos: PhotosConfig, name: string): string {
  return `${photos.baseUrl}/${name}?${photos.sas}`;
}

async function uploadPhoto(photos: PhotosConfig, name: string, blob: Blob): Promise<boolean> {
  try {
    const response = await fetch(photoUrl(photos, name), {
      method: 'PUT',
      headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': 'image/jpeg' },
      body: blob,
    });
    return response.ok;
  } catch {
    return false;
  }
}

/* ── Registration & messages, via Azure Table Storage REST ─────────────── */

interface TeamUser {
  email: string;
  nick: string;
}

interface TeamMessage {
  rowKey: string;
  nick: string;
  text: string;
  postedAt: string;
  /** Blob file name in the photos folder (e.g. `<rowKey>.jpg`) — the image
   *  itself lives in Azure Blob Storage, never in the table. */
  photoName?: string;
}

const PHOTO_MAX_BYTES = 500_000;

const PHOTO_NAME_RE = /^[A-Za-z0-9._-]{1,80}\.jpg$/;

/** Downscale and re-encode a picked image until it fits the size budget. */
async function compressPhoto(file: File): Promise<Blob | null> {
  const bitmap = await createImageBitmap(file).catch(() => null);
  if (!bitmap) return null;
  try {
    let dim = 1600;
    let quality = 0.8;
    for (let attempt = 0; attempt < 6; attempt++) {
      const scale = Math.min(1, dim / Math.max(bitmap.width, bitmap.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(bitmap.width * scale));
      canvas.height = Math.max(1, Math.round(bitmap.height * scale));
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, 'image/jpeg', quality),
      );
      if (blob && blob.size <= PHOTO_MAX_BYTES) return blob;
      dim = Math.round(dim * 0.8);
      quality = Math.max(0.55, quality - 0.07);
    }
    return null;
  } finally {
    bitmap.close();
  }
}

const ODATA_HEADERS = { Accept: 'application/json;odata=nometadata' };

/** OData string literals double their single quotes. */
function odataQuote(value: string): string {
  return value.replace(/'/g, "''");
}

async function lookupUser(tables: TablesConfig, email: string): Promise<TeamUser | null> {
  try {
    const url = `${tables.endpoint}/users(PartitionKey='user',RowKey='${encodeURIComponent(
      odataQuote(email),
    )}')?${tables.usersSas}`;
    const response = await fetch(url, { headers: ODATA_HEADERS });
    if (!response.ok) return null; // 404 → not registered
    const entity = await response.json();
    return { email, nick: typeof entity.NickName === 'string' ? entity.NickName : email };
  } catch {
    return null;
  }
}

async function createUser(tables: TablesConfig, user: TeamUser): Promise<boolean> {
  try {
    const response = await fetch(`${tables.endpoint}/users?${tables.usersSas}`, {
      method: 'POST',
      headers: { ...ODATA_HEADERS, 'Content-Type': 'application/json', Prefer: 'return-no-content' },
      body: JSON.stringify({ PartitionKey: 'user', RowKey: user.email, NickName: user.nick }),
    });
    return response.ok;
  } catch {
    return false;
  }
}

/** Coerce a table entity or WebSocket payload into a message, or reject it. */
function toMessage(raw: unknown): TeamMessage | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const e = raw as Record<string, unknown>;
  const rowKey = typeof e.rowKey === 'string' ? e.rowKey : typeof e.RowKey === 'string' ? e.RowKey : null;
  const nick = typeof e.nick === 'string' ? e.nick : typeof e.NickName === 'string' ? e.NickName : null;
  const text = typeof e.text === 'string' ? e.text : typeof e.Text === 'string' ? e.Text : null;
  const postedAt =
    typeof e.postedAt === 'string' ? e.postedAt : typeof e.PostedAt === 'string' ? e.PostedAt : '';
  let photoName =
    typeof e.photoName === 'string' ? e.photoName : typeof e.PhotoName === 'string' ? e.PhotoName : '';
  if (photoName && !PHOTO_NAME_RE.test(photoName)) photoName = '';
  if (!rowKey || !nick || typeof text !== 'string') return null;
  if (!text.trim() && !photoName) return null;
  return {
    rowKey: rowKey.slice(0, 64),
    nick: nick.slice(0, 40),
    text: text.slice(0, 500),
    postedAt,
    ...(photoName ? { photoName } : {}),
  };
}

/** Fetch messages; pass `beforeKey` to get only rows newer than it.
 *  Row keys are inverted timestamps, so newer rows sort strictly lower —
 *  polls fetch just the delta instead of re-downloading every photo. */
async function fetchMessages(
  tables: TablesConfig,
  beforeKey?: string,
): Promise<TeamMessage[] | null> {
  try {
    // Reactions share this table under another partition — keep them out of the top-50.
    let filter = "PartitionKey eq 'msg'";
    if (beforeKey) filter += ` and RowKey lt '${odataQuote(beforeKey)}'`;
    const response = await fetch(
      `${tables.endpoint}/messages()?$filter=${encodeURIComponent(filter)}&$top=50&${tables.messagesSas}`,
      { headers: ODATA_HEADERS },
    );
    if (!response.ok) return null;
    const body = await response.json();
    if (!Array.isArray(body?.value)) return null;
    return body.value.map(toMessage).filter((m: TeamMessage | null): m is TeamMessage => m !== null);
  } catch {
    return null;
  }
}

async function insertMessage(tables: TablesConfig, message: TeamMessage): Promise<boolean> {
  try {
    const entity: Record<string, string> = {
      PartitionKey: 'msg',
      RowKey: message.rowKey,
      NickName: message.nick,
      Text: message.text,
      PostedAt: message.postedAt,
    };
    if (message.photoName) entity.PhotoName = message.photoName;
    const response = await fetch(`${tables.endpoint}/messages?${tables.messagesSas}`, {
      method: 'POST',
      headers: { ...ODATA_HEADERS, 'Content-Type': 'application/json', Prefer: 'return-no-content' },
      body: JSON.stringify(entity),
    });
    return response.ok;
  } catch {
    return false;
  }
}

/* ── Emoji reactions, stored in the messages table under PK 'react' ────── */

/** The palette. Incoming reactions outside this set are dropped. */
const REACTION_EMOJIS = ['👍', '❤️', '😂', '🎾', '🍹', '🌭'] as const;

interface Reaction {
  /** `${msgKey}~${emoji}~${email}` — one row per user, per emoji, per message. */
  rowKey: string;
  msgKey: string;
  emoji: string;
  nick: string;
}

/** Coerce a table entity or WebSocket payload into a reaction, or reject it. */
function toReaction(raw: unknown): Reaction | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const e = raw as Record<string, unknown>;
  const rowKey = typeof e.rowKey === 'string' ? e.rowKey : typeof e.RowKey === 'string' ? e.RowKey : null;
  const msgKey = typeof e.msgKey === 'string' ? e.msgKey : typeof e.MsgKey === 'string' ? e.MsgKey : null;
  const emoji = typeof e.emoji === 'string' ? e.emoji : typeof e.Emoji === 'string' ? e.Emoji : null;
  const nick = typeof e.nick === 'string' ? e.nick : typeof e.NickName === 'string' ? e.NickName : null;
  if (!rowKey || !msgKey || !nick || !emoji) return null;
  if (!(REACTION_EMOJIS as readonly string[]).includes(emoji)) return null;
  return { rowKey: rowKey.slice(0, 160), msgKey: msgKey.slice(0, 64), emoji, nick: nick.slice(0, 40) };
}

async function fetchReactions(tables: TablesConfig): Promise<Reaction[] | null> {
  try {
    const filter = encodeURIComponent("PartitionKey eq 'react'");
    const response = await fetch(
      `${tables.endpoint}/messages()?$filter=${filter}&${tables.messagesSas}`,
      { headers: ODATA_HEADERS },
    );
    if (!response.ok) return null;
    const body = await response.json();
    if (!Array.isArray(body?.value)) return null;
    return body.value.map(toReaction).filter((r: Reaction | null): r is Reaction => r !== null);
  } catch {
    return null;
  }
}

async function insertReaction(tables: TablesConfig, reaction: Reaction): Promise<boolean> {
  try {
    const response = await fetch(`${tables.endpoint}/messages?${tables.messagesSas}`, {
      method: 'POST',
      headers: { ...ODATA_HEADERS, 'Content-Type': 'application/json', Prefer: 'return-no-content' },
      body: JSON.stringify({
        PartitionKey: 'react',
        RowKey: reaction.rowKey,
        MsgKey: reaction.msgKey,
        Emoji: reaction.emoji,
        NickName: reaction.nick,
      }),
    });
    // 409 means this user already reacted with this emoji — that's a no-op, not a failure.
    return response.ok || response.status === 409;
  } catch {
    return false;
  }
}

/** Union by rowKey — the key encodes (message, emoji, user), so dupes collapse. */
function mergeReactions(current: Reaction[], incoming: Reaction[]): Reaction[] {
  const byKey = new Map<string, Reaction>();
  for (const reaction of [...current, ...incoming]) byKey.set(reaction.rowKey, reaction);
  return [...byKey.values()];
}

/** Reactions for one message, grouped per emoji in palette order. */
function groupReactions(
  reactions: Reaction[],
  msgKey: string,
): { emoji: string; nicks: string[] }[] {
  const groups = new Map<string, string[]>();
  for (const reaction of reactions) {
    if (reaction.msgKey !== msgKey) continue;
    const nicks = groups.get(reaction.emoji) ?? [];
    if (!nicks.includes(reaction.nick)) nicks.push(reaction.nick);
    groups.set(reaction.emoji, nicks);
  }
  return REACTION_EMOJIS.filter((emoji) => groups.has(emoji)).map((emoji) => ({
    emoji,
    nicks: groups.get(emoji)!,
  }));
}

/** Fire-and-forget: ask the relay to email the team about a new post.
 *  Silently does nothing in dev or until ACS is configured server-side. */
function notifyTeam(type: 'scores' | 'message', nick: string, text?: string): void {
  try {
    void fetch('/api/notify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type, nick, text }),
    }).catch(() => {});
  } catch {
    /* notification failure must never surface */
  }
}

/** Inverted-timestamp row key: table sort order becomes newest-first. */
function messageRowKey(): string {
  const inverted = String(9_999_999_999_999 - Date.now()).padStart(13, '0');
  return `${inverted}-${Math.random().toString(36).slice(2, 8)}`;
}

/** Union by rowKey, newest first (row keys are inverted timestamps). */
function mergeMessages(current: TeamMessage[], incoming: TeamMessage[]): TeamMessage[] {
  const byKey = new Map<string, TeamMessage>();
  for (const message of [...current, ...incoming]) byKey.set(message.rowKey, message);
  return [...byKey.values()].sort((a, b) => a.rowKey.localeCompare(b.rowKey)).slice(0, 50);
}

const LOCAL_KEY = 'tlh-team-scores';
const USER_KEY = 'tlh-team-user-email';

function setWinner(set: SetScore): 'us' | 'them' | null {
  if (set.us === null || set.them === null || set.us === set.them) return null;
  return set.us > set.them ? 'us' : 'them';
}

function lineResult(sets: SetScore[]): { us: number; them: number; winner: 'us' | 'them' | null } {
  let us = 0;
  let them = 0;
  for (const set of sets) {
    const winner = setWinner(set);
    if (winner === 'us') us++;
    if (winner === 'them') them++;
  }
  return { us, them, winner: us >= 2 ? 'us' : them >= 2 ? 'them' : null };
}

const POLL_MS = 30_000;

export default function Team() {
  usePageTitle("The Team — Chuck's Mojito Shack");

  const [scores, setScores] = useState<ScoreData>(emptyScores);
  const [config, setConfig] = useState<ScoreStoreConfig | null>(null);
  const [ready, setReady] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  const socketRef = useRef<WebSocket | null>(null);

  /* Registration & messages */
  const [tables, setTables] = useState<TablesConfig | null>(null);
  const [user, setUser] = useState<TeamUser | null>(null);
  const [regEmail, setRegEmail] = useState('');
  const [regNick, setRegNick] = useState('');
  const [regBusy, setRegBusy] = useState(false);
  const [regError, setRegError] = useState<string | null>(null);
  const [messages, setMessages] = useState<TeamMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [posting, setPosting] = useState(false);
  const [reactions, setReactions] = useState<Reaction[]>([]);
  const [pickerFor, setPickerFor] = useState<string | null>(null);
  const [photos, setPhotos] = useState<PhotosConfig | null>(null);
  const [photoDraft, setPhotoDraft] = useState<{ blob: Blob; preview: string } | null>(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const messagesRef = useRef<TeamMessage[]>([]);
  messagesRef.current = messages;

  const blobUrlWithSas = config ? `${config.blobUrl}?${config.sas}` : null;

  const fetchScores = useCallback(async (url: string): Promise<ScoreData | null> => {
    try {
      const response = await fetch(url, { cache: 'no-store' });
      if (!response.ok) return null; // 404 before first save is normal
      return sanitize(await response.json());
    } catch {
      return null;
    }
  }, []);

  /* Initial load: runtime config, then blob + tables (or local fallback). */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { scores: scoreConfig, tables: tableConfig, photos: photoConfig } =
        await loadRuntimeSettings();
      if (cancelled) return;
      setConfig(scoreConfig);
      setTables(tableConfig);
      setPhotos(photoConfig);
      if (scoreConfig) {
        const remote = await fetchScores(`${scoreConfig.blobUrl}?${scoreConfig.sas}`);
        if (cancelled) return;
        if (remote) setScores(remote);
      } else {
        try {
          const stored = window.localStorage.getItem(LOCAL_KEY);
          if (stored) setScores(sanitize(JSON.parse(stored)));
        } catch {
          /* corrupted local data — start fresh */
        }
      }
      if (tableConfig) {
        // Remembered visitor? Confirm they're still in the users table.
        const storedEmail = window.localStorage.getItem(USER_KEY);
        if (storedEmail) {
          const existing = await lookupUser(tableConfig, storedEmail);
          if (cancelled) return;
          if (existing) setUser(existing);
          else window.localStorage.removeItem(USER_KEY);
        }
        const remoteMessages = await fetchMessages(tableConfig);
        if (!cancelled && remoteMessages) setMessages(remoteMessages);
        const remoteReactions = await fetchReactions(tableConfig);
        if (!cancelled && remoteReactions) setReactions(remoteReactions);
      }
      setReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchScores]);

  /* Live push: same-origin WebSocket relay on the App Service. A posted
     score is broadcast to every open page instantly; polling stays as the
     fallback. Connection failure (e.g. local dev) is silent. */
  useEffect(() => {
    if (!config) return;
    let retry: number | null = null;
    let closed = false;

    const connect = () => {
      let socket: WebSocket;
      try {
        const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws';
        socket = new WebSocket(`${scheme}://${window.location.host}/ws/scores`);
      } catch {
        return;
      }
      socketRef.current = socket;
      socket.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          if (message?.type === 'scores') {
            if (!dirtyRef.current) setScores(sanitize(message.data));
          } else if (message?.type === 'message') {
            const incoming = toMessage(message.data);
            if (incoming) setMessages((current) => mergeMessages(current, [incoming]));
          } else if (message?.type === 'reaction') {
            const incoming = toReaction(message.data);
            if (incoming) setReactions((current) => mergeReactions(current, [incoming]));
          }
        } catch {
          /* ignore malformed broadcasts */
        }
      };
      socket.onclose = () => {
        socketRef.current = null;
        if (!closed) retry = window.setTimeout(connect, 5000 + Math.random() * 5000);
      };
      socket.onerror = () => socket.close();
    };

    connect();
    return () => {
      closed = true;
      if (retry !== null) window.clearTimeout(retry);
      socketRef.current?.close();
      socketRef.current = null;
    };
  }, [config]);

  /* Background refresh so shared viewers see new scores — paused mid-edit. */
  useEffect(() => {
    if (!blobUrlWithSas) return;
    const timer = window.setInterval(async () => {
      if (dirtyRef.current) return;
      const remote = await fetchScores(blobUrlWithSas);
      if (remote && !dirtyRef.current) setScores(remote);
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [blobUrlWithSas, fetchScores]);

  /* Message board refresh — the WebSocket usually beats this, but polling
     catches anything missed while a phone was asleep. */
  useEffect(() => {
    if (!tables) return;
    const timer = window.setInterval(async () => {
      // Newest message has the smallest row key — fetch only what's newer,
      // so photos aren't re-downloaded on every poll.
      const newestKey = messagesRef.current[0]?.rowKey;
      const remote = await fetchMessages(tables, newestKey);
      if (remote && remote.length > 0) setMessages((current) => mergeMessages(current, remote));
      const remoteReactions = await fetchReactions(tables);
      if (remoteReactions) setReactions((current) => mergeReactions(current, remoteReactions));
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [tables]);

  const register = async (event: FormEvent) => {
    event.preventDefault();
    if (!tables || regBusy) return;
    const email = regEmail.trim().toLowerCase();
    const nick = regNick.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setRegError("That email doesn't look right.");
      return;
    }
    setRegBusy(true);
    setRegError(null);
    const existing = await lookupUser(tables, email);
    if (existing) {
      setUser(existing);
      window.localStorage.setItem(USER_KEY, email);
      setNotice(`Welcome back, ${existing.nick}.`);
    } else if (!nick) {
      setRegError("You're new here — pick a nickname too, then hit the button again.");
    } else {
      const created = await createUser(tables, { email, nick });
      if (created) {
        setUser({ email, nick });
        window.localStorage.setItem(USER_KEY, email);
        setNotice(`Registered. Welcome to the roster, ${nick}.`);
      } else {
        // The insert can fail while the row still lands (or already existed
        // from a half-finished attempt) — check before declaring defeat.
        const retry = await lookupUser(tables, email);
        if (retry) {
          setUser(retry);
          window.localStorage.setItem(USER_KEY, email);
          setNotice(`Welcome back, ${retry.nick}.`);
        } else {
          setRegError('Could not save your registration — give it a few seconds and try again.');
        }
      }
    }
    setRegBusy(false);
  };

  const signOut = () => {
    window.localStorage.removeItem(USER_KEY);
    setUser(null);
    setRegEmail('');
    setRegNick('');
  };

  const clearPhotoDraft = useCallback(() => {
    setPhotoDraft((current) => {
      if (current) URL.revokeObjectURL(current.preview);
      return null;
    });
  }, []);

  const pickPhoto = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = ''; // allow re-picking the same file
    if (!file) return;
    setPhotoBusy(true);
    const blob = await compressPhoto(file);
    setPhotoBusy(false);
    if (blob) {
      clearPhotoDraft();
      setPhotoDraft({ blob, preview: URL.createObjectURL(blob) });
    } else {
      setNotice('Could not read that photo — try a JPEG or PNG.');
    }
  };

  const postToBoard = async (event: FormEvent) => {
    event.preventDefault();
    if (!tables || !user || posting) return;
    const text = draft.trim().slice(0, 500);
    if (!text && !photoDraft) return;
    setPosting(true);
    const rowKey = messageRowKey();
    let photoName: string | undefined;
    if (photoDraft && photos) {
      photoName = `${rowKey}.jpg`;
      const uploaded = await uploadPhoto(photos, photoName, photoDraft.blob);
      if (!uploaded) {
        setNotice('Could not upload the photo — message not posted. Try again.');
        setPosting(false);
        return;
      }
    }
    const message: TeamMessage = {
      rowKey,
      nick: user.nick,
      text,
      postedAt: new Date().toISOString(),
      ...(photoName ? { photoName } : {}),
    };
    const ok = await insertMessage(tables, message);
    if (ok) {
      setMessages((current) => mergeMessages(current, [message]));
      setDraft('');
      clearPhotoDraft();
      if (socketRef.current?.readyState === WebSocket.OPEN) {
        socketRef.current.send(JSON.stringify({ type: 'message', data: message }));
      }
      notifyTeam('message', user.nick, text || '📷 Photo');
    } else {
      setNotice('Could not post the message — try again.');
    }
    setPosting(false);
  };

  const react = async (msgKey: string, emoji: string) => {
    if (!tables || !user) return;
    setPickerFor(null);
    const reaction: Reaction = {
      rowKey: `${msgKey}~${emoji}~${user.email}`,
      msgKey,
      emoji,
      nick: user.nick,
    };
    // Optimistic: the row key is deterministic, so a re-click just collapses.
    setReactions((current) => mergeReactions(current, [reaction]));
    const ok = await insertReaction(tables, reaction);
    if (ok && socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify({ type: 'reaction', data: reaction }));
    }
  };

  const updateSet = (lineIndex: number, setIndex: number, side: keyof SetScore, value: string) => {
    const parsed = value === '' ? null : Math.max(0, Math.min(99, Number(value)));
    if (parsed !== null && Number.isNaN(parsed)) return;
    setScores((current) => {
      const next: ScoreData = {
        ...current,
        lines: current.lines.map((line, i) =>
          i === lineIndex
            ? line.map((set, j) => (j === setIndex ? { ...set, [side]: parsed } : set))
            : line,
        ),
      };
      return next;
    });
    setDirty(true);
    setNotice(null);
  };

  const save = async () => {
    if (tables && !user) {
      setNotice('Register with your email and nickname above before posting scores.');
      return;
    }
    const payload: ScoreData = {
      ...scores,
      season: SEASON_KEY,
      updatedAt: new Date().toISOString(),
    };
    if (!blobUrlWithSas) {
      window.localStorage.setItem(LOCAL_KEY, JSON.stringify(payload));
      setScores(payload);
      setDirty(false);
      setNotice('Saved on this device only — live sharing is not configured yet.');
      return;
    }
    setSaving(true);
    try {
      const response = await fetch(blobUrlWithSas, {
        method: 'PUT',
        headers: {
          'x-ms-blob-type': 'BlockBlob',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      setScores(payload);
      setDirty(false);
      setNotice('Scores posted — everyone with the link sees them now.');
      if (socketRef.current?.readyState === WebSocket.OPEN) {
        socketRef.current.send(JSON.stringify({ type: 'scores', data: payload }));
      }
      notifyTeam('scores', user?.nick ?? '');
    } catch {
      setNotice('Could not reach the scoreboard storage. Scores are still on screen — try again.');
    } finally {
      setSaving(false);
    }
  };

  const refresh = async () => {
    if (!blobUrlWithSas) return;
    const remote = await fetchScores(blobUrlWithSas);
    if (remote) {
      setScores(remote);
      setDirty(false);
      setNotice('Scoreboard refreshed.');
    }
  };

  const results = scores.lines.map(lineResult);
  const teamUs = results.filter((r) => r.winner === 'us').length;
  const teamThem = results.filter((r) => r.winner === 'them').length;

  // Read the clock once per render so the card and the schedule agree.
  const now = new Date();
  const next = upcomingMatch(now);
  const countdown = next ? daysUntil(next.iso, now) : null;

  const feed = useTennisFeed();
  const roster = groupRoster(feed.roster);
  const rosterAge = feed.fetchedAt ? timeAgo(feed.fetchedAt, now) : '';
  // Anything older than a day means the Pi's scraper has probably stopped.
  const rosterStale =
    feed.source === 'live' &&
    !!feed.fetchedAt &&
    now.getTime() - new Date(feed.fetchedAt).getTime() > 24 * 3600 * 1000;

  return (
    <div className="team-page page-content">
      <div className="team-header">
        <span className="section-tag">Towne Lake Hills Tennis</span>
        <h1 className="team-title">{SEASON.name}</h1>
        <p className="team-meta">
          {SEASON.club} &middot; Roster ID: {SEASON.rosterId} &middot; Division: {SEASON.division}
        </p>
        <p className="team-sub">
          It&apos;s here. The official drinking team with a tennis problem opens the fall season
          <strong> this Saturday</strong>, and Chuck could not be happier about it &mdash; six
          matches, one bye, and a playoff run he has already talked himself into. This page is the
          team&apos;s scoreboard: post the numbers as they land and the whole team sees them.
        </p>
      </div>

      {/* ── Match-day weather warning ── */}
      <div className="storm-warning" role="alert">
        <div className="storm-head">
          <span className="storm-icon" aria-hidden="true">
            🌧️
          </span>
          <div>
            <span className="storm-kicker">Match-day forecast</span>
            <h2 className="storm-title">{WEATHER.headline}</h2>
          </div>
        </div>

        <p className="storm-lede">
          We start at <strong>9:00 AM</strong>, and the morning is the damp part: light drizzle off
          and on from before dawn into late morning, with a <strong>{WEATHER.chance}% chance</strong>{' '}
          of rain right at first serve. Expect <strong>slick lines and heavy balls</strong> early
          &mdash; but this is drizzle, not a downpour: {WEATHER.rainInches} in for the whole day.{' '}
          <strong>No thunderstorms in the forecast</strong> &mdash; the storms that showed up two
          days out fell apart. It dries out from about <strong>{WEATHER.dryFrom}</strong> and the
          afternoon turns sunny. High {WEATHER.high}°F, low {WEATHER.low}°F, gusts to {WEATHER.gusts} mph.
        </p>

        <ul className="storm-hours">
          {WEATHER.hours.map((h) => (
            <li key={h.time} className={`storm-hour${h.storm ? ' storm-hour-bad' : ''}`}>
              <span className="storm-hour-time">{h.time}</span>
              <span className="storm-hour-glyph" aria-hidden="true">
                {h.storm ? '⛈️' : h.pop >= 60 ? '🌧️' : '🌥️'}
              </span>
              <span className="storm-hour-temp">{h.temp}°</span>
              <span className="storm-hour-pop">{h.pop}%</span>
              {h.note && <span className="storm-hour-note">{h.note}</span>}
            </li>
          ))}
        </ul>

        <p className="storm-foot">
          Towel in the bag, dry grips, and a spare shirt &mdash; the courts will be wet even when
          the sky is not. Keep an eye on your phone: wet-court delays are the referee&apos;s call
          and ALTA makes it late. Forecast pulled {WEATHER.pulledOn} from Open-Meteo for the Dallas
          courts, so this one is current.
        </p>
      </div>

      {/* ── Availability: thanks, then the nudge ── */}
      <div className="avail-thanks">
        <span className="avail-thanks-icon" aria-hidden="true">
          🙏
        </span>
        <div>
          <strong>Thank you to the {roster.responded.length} men who put in availability.</strong>
          <p>
            {roster.available.length} in, {roster.unavailable.length} out &mdash; either way you answered, and
            that is the whole ask. You made the captain's job possible and you are the reason there
            is a lineup at all. Genuinely: thank you.
          </p>
        </div>
      </div>

      <div className="avail-alert" role="alert">
        <span className="avail-alert-icon" aria-hidden="true">
          🚨
        </span>
        <div className="avail-alert-body">
          <strong>Still waiting on {roster.hallOfShame.length} of you.</strong>
          <p>
            The season opener is <strong>this Saturday, September 12 at Seven Hills</strong> and{' '}
            {roster.hallOfShame.length} men still have not given the captain a yes or a no. Log in, find
            the dropdown, click one of the three little faces. It takes four seconds.
          </p>
          <a
            className="avail-alert-cta"
            href="https://altatennis.org/Member/Teams/Availability.aspx"
            target="_blank"
            rel="noopener noreferrer"
          >
            Put in your availability &rarr;
          </a>
        </div>
      </div>

      {/* ── Next up ── */}
      <div className="celebrate-card">
        <div className="celebrate-banner">
          <span className="celebrate-confetti" aria-hidden="true">
            🎾 🍹 🎾
          </span>
          <span className="celebrate-kicker">
            {next ? `Week ${next.week} \u00b7 ${next.away ? 'Away' : 'Home'}` : 'Regular Season'}
          </span>
          <h2 className="celebrate-title">{next ? 'NEXT UP' : 'SEASON COMPLETE'}</h2>
          <p className="celebrate-sub">
            {next ? (
              <>
                {next.away ? 'We travel to ' : 'We host '}
                <strong>{next.opponent}</strong>
                {next.opponentCaptain && <> (capt. {next.opponentCaptain})</>} on{' '}
                <strong>{next.label}</strong>
                {countdown !== null && countdown > 1 && <> &mdash; {countdown} days out</>}
                {countdown === 1 && <> &mdash; tomorrow</>}
                {countdown === 0 && <> &mdash; that&apos;s today</>}.{' '}
                {countdown !== null && countdown <= 7 ? (
                  <>
                    The wait is over, gentlemen. Rackets strung, fresh balls, alarms set for 8 AM.
                    Let&apos;s go.
                  </>
                ) : (
                  <>Chuck is already counting down.</>
                )}
              </>
            ) : (
              <>
                All seven weeks are in the books. The scoreboard below stays up, and so does the
                cookout.
              </>
            )}
          </p>
        </div>

        <div className="celebrate-next">
          <div className="match-detail">
            <h3>Season Opener</h3>
            <p>
              <strong>Saturday, September 12 &middot; show up 9:00 AM</strong>
              <br />
              {VENUE.name}
              <br />
              {VENUE.address} &middot; {VENUE.courts}
              <br />
              Lineup published &mdash; all five lines on court at 9:00 AM.
            </p>
            <div className="nav-buttons">
              <a
                className="nav-btn nav-btn-primary"
                href={VENUE.googleMaps}
                target="_blank"
                rel="noopener noreferrer"
              >
                🧭 Navigate to the courts
              </a>
              <a
                className="nav-btn"
                href={VENUE.appleMaps}
                target="_blank"
                rel="noopener noreferrer"
              >
                 Apple Maps
              </a>
            </div>
          </div>
          <div className="match-detail match-tally">
            <h3>Current Match</h3>
            <div className="tally">
              <div className="tally-side">
                <span className="tally-number">{teamUs}</span>
                <span className="tally-name">TLH</span>
              </div>
              <span className="tally-dash">&ndash;</span>
              <div className="tally-side">
                <span className="tally-number">{teamThem}</span>
                <span className="tally-name">{next?.opponent ?? 'Opponent'}</span>
              </div>
            </div>
            <p className="tally-note">Lines won, from the scores posted below.</p>
          </div>
        </div>
      </div>

      {/* ── Hall of Shame ── */}
      <div className="offenders-section">
        <div className="score-heading">
          <h2>The Hall of Shame</h2>
          <p>
            Week 1 at Seven Hills, by the numbers: {roster.available.length} said yes,{' '}
            {roster.unavailable.length} said no, and the {roster.hallOfShame.length} men below have
            produced neither a yes nor a no. Induction is free and the exit is one click away.
          </p>
        </div>

        {/* Freshness, stated plainly — a roster board is only worth as much
            as the last time anything actually read ALTA. */}
        <p className={`roster-freshness${rosterStale ? ' roster-freshness-stale' : ''}`}>
          {feed.source === 'live' ? (
            <>
              <span className="roster-dot" aria-hidden="true">
                {rosterStale ? '🟠' : '🟢'}
              </span>
              Live from ALTA via Chuckles &mdash; last read{' '}
              <strong>{rosterAge}</strong>
              {feed.fetchedAt && (
                <> ({new Date(feed.fetchedAt).toLocaleString()})</>
              )}
              {rosterStale && <> &mdash; the scraper may have stopped.</>}
            </>
          ) : (
            <>
              <span className="roster-dot" aria-hidden="true">
                ⚪
              </span>
              Showing the last known roster &mdash; the live feed is unreachable right now, so
              these numbers are from {ROSTER_AS_OF}.
            </>
          )}
        </p>

        <div className="avail-tally">
          <span className="avail-chip avail-chip-in">🙂 {roster.available.length} in</span>
          <span className="avail-chip avail-chip-out">🙁 {roster.unavailable.length} out</span>
          <span className="avail-chip avail-chip-maybe">🤔 {roster.maybes.length} maybe</span>
          <span className="avail-chip avail-chip-silent">🫥 {roster.silent.length} silent</span>
        </div>

        <ol className="offender-list">
          {roster.hallOfShame.map((p, i) => (
            <li
              key={p.name}
              className={`offender${p.status === 'maybe' ? ' offender-maybe' : ''}`}
            >
              <span className="offender-num">#{String(i + 1).padStart(2, '0')}</span>
              <span className="offender-mug" aria-hidden="true">
                {p.status === 'maybe' ? '🤔' : '🫥'}
              </span>
              <span className="offender-body">
                <span className="offender-name">
                  {p.name}
                  {p.role && <span className="offender-role">{p.role}</span>}
                </span>
                <span className="offender-charge">
                  {p.status === 'maybe'
                    ? 'Clicked "maybe" — technically not a yes or a no'
                    : CHARGES[i % CHARGES.length]}
                </span>
              </span>
            </li>
          ))}
        </ol>

        <p className="offender-foot">
          Induction is not permanent. Click a face on ALTA and Chuck will quietly strike your name
          from the record, no questions asked, mojito privileges fully restored.
        </p>
      </div>

      {/* ── Cookout incident report ── */}
      <div className="cookout-alert" role="alert">
        <span className="cookout-alert-icon" aria-hidden="true">
          🌭
        </span>
        <div>
          <strong>Cookout Duty Notice:</strong> last season ended with not enough bratwursts and a
          box of Hostess Twinkies standing in as the team's fuel strategy. ALTA lists Bring as N/A
          for Week 1, so consider this an away-match reprieve. Nobody is hauling a blender to an
          away match &mdash; ask again about the first home match on September 26.
        </div>
      </div>

      {/* ── Live scoreboard ── */}
      <div className="score-section">
        <div className="score-heading">
          <h2>Line Scores</h2>
          <p>
            This match, line by line. Anyone with this link can post: type the games for each
            set and hit <strong>Post Scores</strong> &mdash; the whole team sees the update
            instantly, and everyone registered gets an email.
          </p>
        </div>

        {ready && !config && (
          <p className="score-mode-banner">
            Live sharing isn't wired up yet &mdash; scores entered here save on this device only.
            (Waiting on the Azure Storage config in <code>appsettings.json</code>.)
          </p>
        )}

        {ready && tables && !user && (
          <div className="signin-card">
            <h3>First, tell us who you are</h3>
            <p>
              Posting scores and messages takes a registered email and nickname. No password
              &mdash; Chuck trusts you, statistically. Returning folks just need the email.
              Registering also signs you up for email alerts when scores or messages land.
            </p>
            <form className="signin-form" onSubmit={register}>
              <input
                type="email"
                required
                placeholder="you@example.com"
                aria-label="Email"
                value={regEmail}
                onChange={(e) => setRegEmail(e.target.value)}
              />
              <input
                type="text"
                maxLength={40}
                placeholder="Nickname (new folks only)"
                aria-label="Nickname"
                value={regNick}
                onChange={(e) => setRegNick(e.target.value)}
              />
              <button type="submit" disabled={regBusy}>
                {regBusy ? 'Checking…' : 'Register / Sign In'}
              </button>
            </form>
            {regError && (
              <p className="signin-error" role="alert">
                {regError}
              </p>
            )}
          </div>
        )}

        {ready && tables && user && (
          <p className="signin-status">
            Posting as <strong>{user.nick}</strong> ({user.email})
            <button type="button" className="signin-switch" onClick={signOut}>
              Not you?
            </button>
          </p>
        )}

        <div className="score-grid" role="table" aria-label="Line scores">
          <div className="score-row score-row-head" role="row">
            <span className="score-cell-line" role="columnheader">
              Line
            </span>
            <span className="score-cell-players" role="columnheader">
              Players
            </span>
            {Array.from({ length: SETS_PER_LINE }, (_, i) => (
              <span key={i} className="score-cell-set" role="columnheader">
                Set {i + 1}
              </span>
            ))}
            <span className="score-cell-result" role="columnheader">
              Result
            </span>
          </div>

          {LINEUP.map((line, lineIndex) => {
            const result = results[lineIndex];
            return (
              <div
                key={line.id}
                className={`score-row${result.winner ? ` line-${result.winner}` : ''}`}
                role="row"
              >
                <span className="score-cell-line">{line.id}</span>
                <span className="score-cell-players">
                  {line.players ? (
                    line.players.map((player) => (
                      <span key={player.name} className="player">
                        <strong>{player.name}</strong>
                        {player.bring && <small>{player.bring}</small>}
                      </span>
                    ))
                  ) : (
                    <span className="player player-tbd">
                      <strong>Lineup not posted</strong>
                      <small>Captain sets this the week of the match</small>
                    </span>
                  )}
                </span>
                {scores.lines[lineIndex].map((set, setIndex) => (
                  <span key={setIndex} className="score-cell-set" data-set={`Set ${setIndex + 1}`}>
                    <label>
                      <span className="visually-hidden">
                        {line.id} set {setIndex + 1} Towne Lake Hills games
                      </span>
                      <input
                        type="number"
                        min={0}
                        max={99}
                        inputMode="numeric"
                        placeholder="–"
                        value={set.us ?? ''}
                        onChange={(e) => updateSet(lineIndex, setIndex, 'us', e.target.value)}
                      />
                    </label>
                    <span className="set-divider">:</span>
                    <label>
                      <span className="visually-hidden">
                        {line.id} set {setIndex + 1} {next?.opponent ?? 'opponent'} games
                      </span>
                      <input
                        type="number"
                        min={0}
                        max={99}
                        inputMode="numeric"
                        placeholder="–"
                        value={set.them ?? ''}
                        onChange={(e) => updateSet(lineIndex, setIndex, 'them', e.target.value)}
                      />
                    </label>
                  </span>
                ))}
                <span className="score-cell-result">
                  {result.winner === 'us' && <span className="result-badge won">TLH ✔</span>}
                  {result.winner === 'them' && <span className="result-badge lost">GLE</span>}
                  {result.winner === null &&
                    (result.us + result.them > 0 ? (
                      <span className="result-badge live">{result.us}–{result.them} sets</span>
                    ) : (
                      <span className="result-badge idle">—</span>
                    ))}
                </span>
              </div>
            );
          })}
        </div>

        <div className="score-actions">
          <button
            type="button"
            className="score-save"
            onClick={save}
            disabled={saving || !ready || (tables !== null && user === null)}
            title={tables && !user ? 'Register above to post scores' : undefined}
          >
            {saving ? 'Posting…' : 'Post Scores'}
          </button>
          {config && (
            <button type="button" className="score-refresh" onClick={refresh} disabled={saving}>
              Refresh
            </button>
          )}
          {dirty && <span className="score-dirty">Unposted changes</span>}
        </div>
        {notice && (
          <p className="score-notice" role="status">
            {notice}
          </p>
        )}
        <p className="score-footnote">
          Scoreboard updates automatically every 30 seconds.{' '}
          {scores.updatedAt
            ? `Last posted: ${new Date(scores.updatedAt).toLocaleString()}`
            : 'Nothing posted yet this season.'}
        </p>
      </div>

      {/* ── Team messages ── */}
      <div className="messages-section">
        <div className="score-heading">
          <h2>Team Messages</h2>
          <p>Rain updates, ride shares, and respectful trash talk. Photos welcome. Newest first.</p>
        </div>

        {tables ? (
          user ? (
            <form className="message-form" onSubmit={postToBoard}>
              <textarea
                rows={3}
                maxLength={500}
                placeholder={`Say something, ${user.nick}…`}
                aria-label="New message"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
              />
              {photoDraft && (
                <div className="photo-preview">
                  <img src={photoDraft.preview} alt="Photo attachment preview" />
                  <button
                    type="button"
                    className="photo-remove"
                    aria-label="Remove photo"
                    onClick={clearPhotoDraft}
                  >
                    ✕
                  </button>
                </div>
              )}
              <div className="message-form-actions">
                {photos && (
                  <label className={`photo-attach${photoBusy ? ' photo-attach-busy' : ''}`}>
                    {photoBusy ? 'Preparing…' : '📷 Add Photo'}
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      disabled={photoBusy || posting}
                      onChange={pickPhoto}
                    />
                  </label>
                )}
                <button
                  type="submit"
                  className="score-save"
                  disabled={posting || photoBusy || (!draft.trim() && !photoDraft)}
                >
                  {posting ? 'Posting…' : 'Post Message'}
                </button>
              </div>
            </form>
          ) : (
            <p className="messages-hint">Register above to join the conversation.</p>
          )
        ) : (
          <p className="messages-hint">Messages aren't configured yet.</p>
        )}

        {messages.length === 0 ? (
          <p className="messages-empty">Nothing yet. Be the first to say something regrettable.</p>
        ) : (
          <ul className="message-list">
            {messages.map((message) => {
              const chips = groupReactions(reactions, message.rowKey);
              return (
                <li key={message.rowKey} className="message">
                  <div className="message-meta">
                    <strong>{message.nick}</strong>
                    <time dateTime={message.postedAt}>
                      {message.postedAt
                        ? new Date(message.postedAt).toLocaleString([], {
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                          })
                        : ''}
                    </time>
                  </div>
                  {message.text && <p className="message-text">{message.text}</p>}
                  {message.photoName && photos && (
                    <a
                      className="message-photo-link"
                      href={photoUrl(photos, message.photoName)}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <img
                        className="message-photo"
                        src={photoUrl(photos, message.photoName)}
                        alt={`Photo from ${message.nick}`}
                        loading="lazy"
                      />
                    </a>
                  )}
                  {(chips.length > 0 || user) && (
                    <div className="message-reactions">
                      {chips.map(({ emoji, nicks }) => (
                        <button
                          key={emoji}
                          type="button"
                          className={`reaction-chip${
                            user && nicks.includes(user.nick) ? ' reaction-chip-mine' : ''
                          }`}
                          title={nicks.join(', ')}
                          aria-label={`${emoji} from ${nicks.join(', ')}${
                            user ? ' — click to react too' : ''
                          }`}
                          onClick={() => react(message.rowKey, emoji)}
                        >
                          <span aria-hidden="true">{emoji}</span>
                          <span className="reaction-count">{nicks.length}</span>
                        </button>
                      ))}
                      {user && (
                        <span className="reaction-add-wrap">
                          <button
                            type="button"
                            className="reaction-add"
                            aria-label="Add a reaction"
                            aria-expanded={pickerFor === message.rowKey}
                            onClick={() =>
                              setPickerFor((open) =>
                                open === message.rowKey ? null : message.rowKey,
                              )
                            }
                          >
                            +🙂
                          </button>
                          {pickerFor === message.rowKey && (
                            <span className="reaction-picker">
                              {REACTION_EMOJIS.map((emoji) => (
                                <button
                                  key={emoji}
                                  type="button"
                                  className="reaction-option"
                                  aria-label={`React with ${emoji}`}
                                  onClick={() => react(message.rowKey, emoji)}
                                >
                                  {emoji}
                                </button>
                              ))}
                            </span>
                          )}
                        </span>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* ── Fall schedule ── */}
      <div className="schedule-section">
        <div className="score-heading">
          <h2>The Fall Schedule</h2>
          <p>
            Seven weeks in Division {SEASON.division}, six of them with a match. Coordinator is{' '}
            {SEASON.coordinator}; {SEASON.captain} captains, {SEASON.coCaptain} co-captains.
            Chuck's only note: away matches require a cooler with a functioning lid.
          </p>
        </div>

        <ol className="sched-list">
          {SCHEDULE.map((m) => {
            const isNext = next?.week === m.week;
            const past = !m.bye && m.iso < todayIso(now);
            return (
              <li
                key={m.week}
                className={`sched-row${isNext ? ' sched-row-next' : ''}${
                  past ? ' sched-row-past' : ''
                }${m.bye ? ' sched-row-bye' : ''}`}
                aria-current={isNext ? 'date' : undefined}
              >
                <span className="sched-week">Wk {m.week}</span>
                <span className="sched-date">{m.label}</span>
                {m.bye ? (
                  <span className="sched-opp sched-opp-bye">Bye week &mdash; no match</span>
                ) : (
                  <>
                    <span className={`sched-side${m.away ? ' sched-side-away' : ''}`}>
                      {m.away ? 'AT' : 'VS'}
                    </span>
                    <span className="sched-opp">
                      {m.opponent}
                      {m.opponentCaptain && <small> ({m.opponentCaptain})</small>}
                    </span>
                  </>
                )}
                {isNext && <span className="sched-flag">Next up</span>}
              </li>
            );
          })}
        </ol>

        <p className="sched-foot">
          Playoffs run October 31 &ndash; November 14 for the teams that make it. Lineups are set
          by the captain each week and are not posted yet.
        </p>
      </div>

    </div>
  );
}
